// Fire-and-forget signal reporting for the Reader's Paper personalization.
// See docs/reader-paper.md — this is the client half of PP-1.
//
// Design: the client only names the event, not the weight. Weights live
// server-side so no client can inflate its own influence. All calls are
// fire-and-forget — the caller never awaits. Failures are swallowed
// silently; the paper's scoring works fine with imperfect signal capture.
//
// Rate control is server-side (60s cooldown per user+post+event), so the
// client can be liberal about firing.

import type { Api } from './api';

export type SignalEvent =
    | 'dwell_s'    // 5-20s on the post view, or just clicked into it
    | 'dwell_m'    // 20-60s or scrolled past 50%
    | 'dwell_l'    // >60s, reached end, or opened comments
    | 'recommend'  // toggled recommend on
    | 'comment'    // posted a comment
    | 'rsvp'       // yes-RSVP'd to an event
    | 'share';     // used the native share button

/** Fire-and-forget. Never throws. */
export function fireSignal(api: Api, postId: string, event: SignalEvent): void {
    // Don't await — we never want signal delivery to block a user action.
    // The server responds 202 on both success and cooldown-dedup.
    api.post('/api/reader-signals', { post_id: postId, event }).catch(() => {
        /* swallowed on purpose — a missing signal doesn't break anything */
    });
}

/**
 * Dwell timer helper. Tracks time-in-viewport and fires escalating dwell
 * signals as thresholds are crossed. Returns a cleanup fn — call it on
 * component unmount so we don't leak timers.
 *
 * Firing pattern (each threshold fires exactly once per mount):
 *   5s   → dwell_s   ("glanced, decided it was interesting")
 *   20s  → dwell_m   ("actually reading")
 *   60s  → dwell_l   ("deep engagement")
 *
 * Timer pauses when the element leaves the viewport (via IntersectionObserver)
 * and resumes when it comes back — so scrolling past doesn't count, but
 * dwelling on-screen does.
 */
export function trackDwell(el: Element, api: Api, postId: string): () => void {
    let visibleSince: number | null = null;
    let accumulatedMs = 0;
    const fired = new Set<SignalEvent>();

    const check = () => {
        const total = accumulatedMs + (visibleSince ? Date.now() - visibleSince : 0);
        if (total >= 5_000  && !fired.has('dwell_s')) { fired.add('dwell_s'); fireSignal(api, postId, 'dwell_s'); }
        if (total >= 20_000 && !fired.has('dwell_m')) { fired.add('dwell_m'); fireSignal(api, postId, 'dwell_m'); }
        if (total >= 60_000 && !fired.has('dwell_l')) { fired.add('dwell_l'); fireSignal(api, postId, 'dwell_l'); }
    };

    // Poll every 5s to check thresholds — cheaper than three separate timers
    // per card and good enough resolution for coarse dwell tiers.
    const pollHandle = window.setInterval(check, 5_000);

    const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !visibleSince) {
            visibleSince = Date.now();
        } else if (!entry.isIntersecting && visibleSince) {
            accumulatedMs += Date.now() - visibleSince;
            visibleSince = null;
            check(); // check on leaving viewport too, in case a threshold was crossed
        }
    }, { threshold: 0.5 }); // >=50% of card visible
    observer.observe(el);

    return () => {
        window.clearInterval(pollHandle);
        observer.disconnect();
        // Final check on unmount so a card the user scrolled off + navigated
        // away from still credits its dwell time.
        check();
    };
}
